<script setup lang="ts">
// NPM
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import { BaseBadge, BaseButton, BaseIcon } from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores
import { useUgcsStore } from '@/stores/ugcs';
import { useCommonStore } from '@/stores/common';
import { useUserStore } from '@/stores/user';
import type { UserDashboardGetAllUgcsResponseType } from '@magma-app/magma-types/all';
const ugcsStore = useUgcsStore();
const commonStore = useCommonStore();
const userStore = useUserStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  ugc: {
    type: Object as PropType<
      UserDashboardGetAllUgcsResponseType['validated'][number]
    >,
    required: false,
    default: null,
  },
  completed: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// Reactive variables
const isSaving = ref(false);

// Computed
const completedUgcStatus = computed(() => {
  if (props.ugc?.status === 'toValidate') {
    return { color: 'yellow', text: t('generic.validation.pending') };
  } else if (props.ugc?.status === 'rejected') {
    return { color: 'red', text: t('generic.validation.declined') };
  } else {
    return { color: 'green', text: t('generic.validation.completed') };
  }
});

// Event emitters
const emit = defineEmits(['close']);

// Event listeners
const onClose = () => {
  emit('close');
};

const onContactAdmin = async () => {
  window.open(
    `mailto:${userStore.user?.organization?.notificationEmail}`,
    '_blank'
  );

  AnalyticsHelper.trackEvent('ask_support', {
    event_properties: 'ugc',
    user_properties: 'helper',
  });
};

const onStartingUgc = async (ugc?: { status?: string | null; id?: number }) => {
  if (isSaving.value) return;
  isSaving.value = true;

  if (!ugc?.status && ugc?.id) {
    await ugcsStore.startUgc(ugc.id);

    AnalyticsHelper.trackEvent('ugc_doing', {
      user_properties: 'helper',
    });
  }

  isSaving.value = false;
};

const onValidatingUgc = async (ugc?: { userUgcId?: number | null }) => {
  if (isSaving.value) return;
  isSaving.value = true;

  if (ugc?.userUgcId) {
    await ugcsStore.accomplishUgc(ugc.userUgcId);

    AnalyticsHelper.trackEvent('ugc_done', {
      user_properties: 'helper',
    });
  }

  commonStore.setManageUgcsModal({
    open: true,
    item: ugc,
    role: 'ugc-completed',
    title: t('generic.completedUgc'),
  });

  isSaving.value = false;
};
</script>

<template>
  <div class="flex h-full w-full flex-col justify-between">
    <div>
      <div
        class="flex cursor-pointer content-center"
        :class="'mb-4'"
        @click="onClose"
      >
        <BaseIcon color="#5F6C85" icon="chevron-left" />

        <span
          class="ml-1 text-mgm-txt-sm font-normal text-foreground-default"
          >{{
            completed ? $t('generic.completedUgcs') : $t('generic.allUgcs')
          }}</span
        >
      </div>

      <div v-if="ugc" class="relative">
        <img
          class="mb-8 h-[25vh] w-full rounded-[5px] border border-transparent object-cover"
          :src="ugc.pictureUrl ? ugc.pictureUrl : '/img/ugc_lg.png'"
        />
        <span
          class="absolute inset-0 flex items-center justify-center text-mgm-txt-sm font-medium text-foreground-brand-default"
          >{{ ugc?.points }}</span
        >
      </div>

      <img
        v-else
        class="mb-8 h-[30vh] w-full rounded-[5px] border border-transparent object-cover"
        :src="
          props.ugc?.pictureUrl ? props.ugc?.pictureUrl : '/img/reward_lg.png'
        "
      />

      <div class="flex items-start justify-between">
        <h3
          class="mr-4 text-mgm-txt-lg font-medium text-foreground-emphasis"
          :class="{ 'max-w-[80%]': !ugc }"
        >
          {{ ugc?.name }}
        </h3>
      </div>

      <BaseBadge
        v-if="ugc && completed"
        class="mt-2"
        :color="completedUgcStatus.color"
        :text="completedUgcStatus.text"
        size="md"
      ></BaseBadge>

      <div
        class="rich-text-content mt-4 text-mgm-txt-sm font-normal text-foreground-default"
        v-html="props.ugc?.description"
      ></div>
    </div>

    <div class="sticky bottom-0 bg-background-default py-6">
      <template v-if="ugc">
        <BaseButton
          v-if="completed && ugc.status === 'toValidate'"
          :text="$t('generic.adminContact')"
          class="mt-2 w-full"
          size="md"
          state="scdr"
          @click="onContactAdmin"
        />
        <BaseButton
          class="mb-2 w-full"
          :disabled="props.ugc?.status === 'interested' || isSaving"
          :text="$t('generic.cta.enroll')"
          @click="onStartingUgc(props.ugc)"
        />

        <BaseButton
          class="w-full"
          :disabled="!props.ugc?.status || isSaving"
          :text="$t('generic.done')"
          @click="onValidatingUgc(props.ugc)"
        />
      </template>
    </div>
  </div>
</template>
